import S, { maybeShape } from 'common/service/sanctuary';
import { isEmpty, is } from 'ramda';
import { AjaxError } from 'rxjs/ajax';
import React from 'react';
import { node, bool, shape, func } from 'prop-types';
import { defineMessages, FormattedMessage } from 'react-intl';
import Toolbar, { ToolbarRow, ToolbarSection } from '../../toolbar';
import { Vbox } from '../../layout';
import Summary from './summary';
import Empty from '../empty';
import Error, { ErrorTitle } from '../error';
import withErrorBoundary from '../../../components/withErrorBoundary';
import { PublicError } from '../../../service/error';
import { getAjaxErrorBody, getAjaxErrorTitle } from '../resource/resource';

const collectionMessages = defineMessages({
  loading: {
    id: 'mdc.wrappers.collection.loading',
    defaultMessage: 'Loading ...',
  },
});

const getErrorTitle = (error) => {
  if (is(PublicError, error)) {
    return error.title;
  }

  if (is(AjaxError, error)) {
    return getAjaxErrorTitle(error);
  }

  throw error;
};

const getErrorBody = (error) => {
  if (is(PublicError, error)) {
    return error.message;
  }

  if (is(AjaxError, error)) {
    return getAjaxErrorBody(error);
  }

  throw error;
};

const Loading = ({ loading, message }) => loading
  && (message || <FormattedMessage {...collectionMessages.loading} />);

const CollectionTool = ({
  context,
  children,
}) => {
  if (is(Function, children)) {
    return S.maybeToNullable(S.map(
      children,
      S.chain(S.eitherToMaybe, context),
    ));
  }

  return children;
};

const CollectionWrapper = ({
  loading,
  context, // Maybe (Either (List a))
  pagination,
  tools,
  messages,
  children,
}) => ([
  <Toolbar key="toolbar">
    <ToolbarRow>
      <ToolbarSection align={ToolbarSection.ALIGN_START} shrink>
        <div className="collection-summary">
          {S.maybe(
            <Loading loading={loading} message={messages.loading} />,
            S.either(
              error => <ErrorTitle title={getErrorTitle(error)} />,
              collection => <Summary collection={collection} messages={messages} />,
            ),
            context,
          )}
        </div>
      </ToolbarSection>
      {tools && (
        <ToolbarSection align={ToolbarSection.ALIGN_END}>
          {is(Array, tools)
            ? tools.map((tool, key) => (
              // eslint-disable-next-line react/no-array-index-key
              <CollectionTool key={key} context={context}>{tool}</CollectionTool>
            ))
            : <CollectionTool context={context}>{tools}</CollectionTool>
          }
        </ToolbarSection>
      )}
    </ToolbarRow>
  </Toolbar>,
  <Vbox key="content">
    {S.maybeToNullable(S.map(S.either(
      error => <Error>{getErrorBody(error)}</Error>,
      collection => (isEmpty(collection)
        ? <Empty reasons={messages.emptyReasons} />
        : is(Function, children) ? children(collection) : children
      ),
    ), context))}
  </Vbox>,
  pagination && React.cloneElement(pagination, {
    key: 'pagination',
  }),
]);

CollectionWrapper.propTypes = {
  loading: bool,
  context: maybeShape,
  messages: shape({
    loading: node,
    summary: func,
    empty: node,
    emptyReasons: node,
  }),
};

CollectionWrapper.defaultProps = {
  loading: false,
  messages: {},
};

export default withErrorBoundary(CollectionWrapper);
